import React, { useMemo, useState } from 'react';
import {
  Button,
  Empty,
  Input,
  Modal,
  Progress,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import { CloudUploadOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  MediaAssetDto,
  MediaCategory,
  uploadMediaAsset,
  useDeleteMedia,
  useMediaAssets,
} from '@shared/api/hooks/useMediaAssets';

interface MediaSelectorProps {
  category: MediaCategory;
  value?: string | null; // currently-selected asset URL
  onChange?: (url: string | null, asset: MediaAssetDto | null) => void;
  /** Allow the operator to delete library entries (admin only). Default false. */
  allowDelete?: boolean;
  placeholder?: string;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

/**
 * Unified BO media library picker. Modal with grid of pre-uploaded assets
 * filtered by category, search by label, plus inline upload of a new asset.
 *
 * Usage:
 *   <MediaSelector category="TEAM_LOGO" value={form.homeTeamLogo}
 *     onChange={(url) => form.setFieldValue('homeTeamLogo', url)} />
 */
export function MediaSelector({
  category,
  value,
  onChange,
  allowDelete = false,
  placeholder = 'Scegli foto/immagine dalla libreria',
}: MediaSelectorProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [uploadingLabel, setUploadingLabel] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { data: assets = [], isLoading, refetch } = useMediaAssets({ category });
  const deleteMutation = useDeleteMedia();

  const filtered = useMemo(() => {
    if (!search.trim()) return assets;
    const q = search.toLowerCase();
    return assets.filter((a) => a.label.toLowerCase().includes(q));
  }, [assets, search]);

  const selectedAsset = assets.find((a) => a.url === value) ?? null;

  const handleUpload = async (file: File): Promise<boolean> => {
    const label = uploadingLabel.trim() || file.name.replace(/\.[^.]+$/, '');
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const asset = await uploadMediaAsset(category, label, file, setUploadProgress);
      message.success(`Caricato: ${asset.label}`);
      setUploadingLabel('');
      await refetch();
      onChange?.(asset.url, asset);
      setOpen(false);
    } catch (err) {
      const e = err as Error;
      message.error(`Upload fallito: ${e.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
    return false; // prevent default antd upload
  };

  const onSelect = (asset: MediaAssetDto): void => {
    onChange?.(asset.url, asset);
    setOpen(false);
  };

  const onClear = (): void => {
    onChange?.(null, null);
  };

  const onDeleteAsset = async (asset: MediaAssetDto, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      await deleteMutation.mutateAsync(asset.id);
      message.success('Asset eliminato');
      if (asset.url === value) onClear();
    } catch {
      message.error('Eliminazione fallita');
    }
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Button
          icon={<PictureOutlined />}
          onClick={() => setOpen(true)}
          style={{ flex: 1, textAlign: 'left' }}
        >
          {selectedAsset ? selectedAsset.label : placeholder}
        </Button>
        {value ? (
          <Button icon={<DeleteOutlined />} danger onClick={onClear} title="Rimuovi" />
        ) : null}
      </Space.Compact>

      {value ? (
        <div style={{ marginTop: 8 }}>
          <img
            src={value}
            alt="immagine selezionata"
            style={{
              maxHeight: 64,
              maxWidth: 120,
              objectFit: 'contain',
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              padding: 4,
              background: '#fff',
            }}
          />
        </div>
      ) : null}

      <Modal
        title={`Libreria media — ${humanCategory(category)}`}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={780}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Input.Search
              placeholder="Cerca per nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
            <Space direction="vertical" align="end" size={4}>
              <Space.Compact>
                <Input
                  placeholder="Etichetta (es. Avellino)"
                  value={uploadingLabel}
                  onChange={(e) => setUploadingLabel(e.target.value)}
                  style={{ width: 200 }}
                />
                <Upload
                  accept={ACCEPTED_TYPES.join(',')}
                  showUploadList={false}
                  beforeUpload={handleUpload}
                  disabled={isUploading}
                  fileList={[] as UploadFile[]}
                >
                  <Button icon={<CloudUploadOutlined />} loading={isUploading} type="primary">
                    Carica nuovo
                  </Button>
                </Upload>
              </Space.Compact>
              {isUploading ? (
                <Progress
                  percent={uploadProgress}
                  size="small"
                  style={{ width: 200 }}
                  aria-label="Progresso upload"
                />
              ) : null}
            </Space>
          </Space>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin />
            </div>
          ) : filtered.length === 0 ? (
            <Empty description="Nessun media in libreria. Carica il primo." />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 12,
              }}
            >
              {filtered.map((asset) => {
                const isSelected = asset.url === value;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => onSelect(asset)}
                    style={{
                      position: 'relative',
                      border: isSelected ? '2px solid #1677ff' : '1px solid #f0f0f0',
                      borderRadius: 6,
                      padding: 8,
                      background: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: 90,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fafafa',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={asset.url}
                        alt={asset.label}
                        style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                    <Typography.Text
                      style={{ fontSize: 12, textAlign: 'center' }}
                      ellipsis={{ tooltip: asset.label }}
                    >
                      {asset.label}
                    </Typography.Text>
                    {allowDelete ? (
                      <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        loading={deleteMutation.isPending}
                        onClick={(e) => onDeleteAsset(asset, e)}
                        style={{ position: 'absolute', top: 4, right: 4 }}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
}

function humanCategory(c: MediaCategory): string {
  return (
    {
      TEAM_LOGO: 'Loghi squadre',
      SPONSOR_LOGO: 'Loghi sponsor',
      PARTNER_LOGO: 'Loghi partner',
      COMPETITION_LOGO: 'Loghi competizione',
      HERO_BANNER: 'Banner home',
      OTHER: 'Altro',
      PLAYER_PHOTO: 'Foto giocatori',
      TEAM_PHOTO: 'Foto squadra',
      FANZONE_PHOTO: 'Foto fanzone',
    }[c] ?? c
  );
}
